import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setSubmitting,
  setPrinterList,
  setPrinterForm,
  updatePrinterField,
  setShowRegistrationModal,
  setShowEditModal,
  closeRegistrationModalAndReset,
  closeEditModalAndReset,
  updatePrinterStatus,
} from '../../store/slices/hardware/printerSlice';

import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import useModals from '../useModals';
import PrinterService from '../../services/hardware/PrinterService';
import { PrinterRegistrationForm } from '../../types/hardware/PrinterTypes';
import { getSaleCopiesValidationError } from '../../features/printer/printerSaleCopies';

const usePrinter = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();

  const {
    form,
    list,
    isSubmitting,
    error,
    showRegistrationModal,
    showEditModal,
  } = useSelector((state: RootState) => state.printer);

  const fetchAllPrinters = async () => {
    try {
      showLoadingIndicator();
      const response = await PrinterService.getAll();
      const { data } = response;

      dispatch(setPrinterList(data));
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const fetchPrinterById = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await PrinterService.findById(id);
      const { data } = response;

      dispatch(setPrinterForm(data));
      dispatch(setShowEditModal(true));
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const savePrinter = async (form: PrinterRegistrationForm) => {
    const validationError = getSaleCopiesValidationError(form.saleCopies);
    if (validationError) {
      showAlert('error', 'Cantidad de copias inválida', validationError);
      return;
    }

    const request = {
      ...form,
      saleCopies: Number(form.saleCopies),
    };

    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      let response;

      if ((form as any).id) {
        response = await PrinterService.update((form as any).id, request);
      } else {
        response = await PrinterService.create(request);
      }

      const { title, message, type } = response;
      openModal(type, { title, message });

      if ((form as any).id) {
        dispatch(closeEditModalAndReset());
      } else {
        dispatch(closeRegistrationModalAndReset());
      }

      await fetchAllPrinters();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  const toggleStatus = async (id: number) => {
    try {
      const response = await PrinterService.toggleStatus(id);
      const { title, message, type, data } = response;

      showAlert(type, title, message);
      dispatch(updatePrinterStatus(data));
    } catch (error) {
      handle(error);
    }
  };

  const updateField = <K extends keyof PrinterRegistrationForm>(
    field: K,
    value: PrinterRegistrationForm[K]
  ) => {
    dispatch(updatePrinterField({ field, value }));
  };

  const openRegistrationModal = () => {
    dispatch(setShowRegistrationModal(true));
  };

  const closeRegistrationModal = () => {
    dispatch(closeRegistrationModalAndReset());
  };

  return {
    form,
    list,
    isSubmitting,
    error,
    showRegistrationModal,
    showEditModal,

    updateField,
    fetchAllPrinters,
    fetchPrinterById,
    savePrinter,
    toggleStatus,

    openRegistrationModal,
    closeRegistrationModal,
  };
};

export default usePrinter;
